import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ERAS, SCENES, AGNES } from "@/lib/images";

const ERA_DATA = [
  {
    year: "1919",
    title: "A New Life Begins",
    text: "In a world still healing from the Great War, Agnes was born. Horse-drawn carts shared the roads with the very first motorcars, and the wireless was a marvel. A century of history was about to unfold around her.",
    img: "/images/agnes-1919-portrait.jpg",
    grade: "saturate-90",
    imgPos: "object-center",
    imgStyle: { objectFit: "contain", objectPosition: "center center", backgroundColor: "#1e2e20" },
  },
  {
    year: "1930s",
    title: "Between Two Wars",
    text: "She grew up through the roar and the hardship of the interwar years — a girl of grit and grace, learning the values of family and quiet strength that would carry her across decades.",
    img: "/images/agnes-1930s-portrait.jpg",
    grade: "sepia-[0.4] contrast-105 brightness-75",
    imgPos: "object-center",
    imgStyle: { objectFit: "contain", objectPosition: "center center", backgroundColor: "#1e2e20" },
    photos: [
      { src: "/images/agnes-1930s-street.jpg", caption: "Out on the town", objPos: "object-center" },
      { src: "/images/agnes-1930s-portrait.jpg", caption: "A quiet afternoon", objPos: "object-center" },
    ],
  },
  {
    year: "1950s",
    title: "Building a Family",
    text: "Post-war optimism, new colours in the world, music on the radio. Agnes built a home and a family — the matriarch whose love would one day stretch across five generations.",
    img: "/images/agnes-1950s-winter.jpg",
    grade: "saturate-[0.85] contrast-105",
    imgPos: "object-center",
    imgStyle: { objectFit: "contain", objectPosition: "center center", backgroundColor: "#1e2e20" },
    photos: [
      { src: "/images/agnes-1950s-family.jpg", caption: "Family roots" },
    ],
  },
  {
    year: "1990s",
    title: "Matriarch of Generations",
    text: "Decades of love, laughter, and family gatherings. Agnes stood at the centre of it all — steady, warm, and radiant. The woman whose quiet grace shaped everyone around her.",
    img: "/images/agnes-1990s-family.jpg",
    grade: "saturate-90 contrast-105",
    imgPos: "object-center",
    imgStyle: { objectFit: "contain", objectPosition: "center center", backgroundColor: "#1e2e20" },
  },
  {
    year: "Today",
    title: "Rolling Hills & Waterfalls",
    text: "Through it all, the green hills and falling water of home stayed constant. Still here. Still smiling. Still the brightest soul in any room — as vibrant at 107 as ever.",
    img: "/images/agnes-today-granny.jpg",
    grade: "saturate-105",
    imgPos: "object-center",
    imgStyle: { objectFit: "contain", objectPosition: "center center", backgroundColor: "#1e2e20" },
  },
  {
    year: "Now → AI",
    title: "From Candlelight to Code",
    text: "She was born before sliced bread and television — and lives to see artificial intelligence, spaceflight, and a connected world. One remarkable life, spanning candlelight to code.",
    img: "/images/agnes-boxing.jpg",
    grade: "saturate-105",
    imgPos: "object-center",
    imgStyle: { objectFit: "contain", objectPosition: "center center", backgroundColor: "#1e2e20" },
  },
];

function EraLayer({ img, grade, opacity, imgPos = "object-top", imgStyle = {} }) {
  return (
    <motion.div style={{ opacity }} className="absolute inset-0">
      <img src={img} alt="" style={imgStyle} className={`h-full w-full object-cover ${imgPos} ${grade}`} />
      <div className="absolute inset-0 bg-gradient-to-r from-forest/85 via-forest/40 to-transparent" />
      <div className="absolute inset-0 bg-forest/25" />
    </motion.div>
  );
}

export default function Timeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  const o0 = useTransform(scrollYProgress, [0, 0.10, 0.17], [1, 1, 0]);
  const o1 = useTransform(scrollYProgress, [0.10, 0.17, 0.27, 0.33], [0, 1, 1, 0]);
  const o2 = useTransform(scrollYProgress, [0.27, 0.33, 0.43, 0.50], [0, 1, 1, 0]);
  const o3 = useTransform(scrollYProgress, [0.43, 0.50, 0.60, 0.67], [0, 1, 1, 0]);
  const o4 = useTransform(scrollYProgress, [0.60, 0.67, 0.77, 0.83], [0, 1, 1, 0]);
  const o5 = useTransform(scrollYProgress, [0.77, 0.83, 1], [0, 1, 1]);
  const opacities = [o0, o1, o2, o3, o4, o5];
  const barWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section ref={ref} data-testid="timeline-section" style={{ height: `${ERA_DATA.length * 100}vh` }} className="relative grain bg-forest">
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {ERA_DATA.map((era, i) => (
          <EraLayer key={i} img={era.img} grade={era.grade} opacity={opacities[i]} imgPos={era.imgPos} imgStyle={era.imgStyle} />
        ))}

        {/* fixed heading + progress */}
        <div className="absolute left-0 top-0 z-20 w-full px-6 pt-10 md:px-12">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">A Century Lived</p>
          <div className="mt-3 h-1 w-full max-w-md overflow-hidden rounded-full bg-cream/20">
            <motion.div style={{ width: barWidth }} className="h-full rounded-full bg-gold" />
          </div>
        </div>
      </div>

      {/* scrolling text columns over the sticky background */}
      <div className="absolute inset-0">
        {ERA_DATA.map((era, i) => (
          <div key={i} className={`relative flex h-screen items-center px-6 md:px-16 ${i % 2 ? "justify-end" : "justify-start"}`}>
            {/* Photos: absolutely positioned on the opposite side from the text card */}
            {era.photos && (
              <motion.div
                initial={{ opacity: 0, x: i % 2 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.5 }}
                transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                className={`absolute top-1/2 -translate-y-1/2 hidden md:flex items-end gap-4 ${i % 2 ? "left-8" : "right-8"}`}
              >
                {era.photos.map((p, pi) => (
                  <div
                    key={pi}
                    className={`relative overflow-hidden rounded-xl shadow-2xl border-4 border-cream/60 ${pi === 0 ? "w-48 lg:w-60" : "w-36 lg:w-48"} ${pi % 2 === 0 ? "-rotate-2" : "rotate-2"}`}
                  >
                    <img
                      src={p.src}
                      alt={p.caption}
                      className={`w-full object-cover ${p.objPos || "object-top"} sepia-[0.35] contrast-110`}
                      style={{ aspectRatio: "3/4" }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-forest/65 px-3 py-2 backdrop-blur-sm">
                      <p className="font-hand text-sm text-cream leading-tight">{p.caption}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.6 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-md rounded-2xl border border-cream/30 bg-cream/85 p-7 backdrop-blur-md md:p-9"
            >
              <span className="font-serif text-6xl font-bold tracking-tight text-terracotta md:text-7xl">{era.year}</span>
              <h3 className="mt-3 font-serif text-2xl font-semibold text-forest md:text-3xl">{era.title}</h3>
              <p className="mt-4 leading-relaxed text-forest/80">{era.text}</p>
            </motion.div>
          </div>
        ))}
      </div>
    </section>
  );
}
